<template>
    <div>
        <div :id="externalResultsSource === 'camera' ? 'cameraResultsTop' : null" class="vgtc-card mt-md-5 p-4 d-flex flex-column">
        <div class="search-results__overview d-flex justify-content-between align-items-center pb-4 mb-4">
            <div class="d-flex flex-column text-nowrap flex-lg-row flex-grow-1">
                <div class="d-flex flex-grow-1">
                    <SearchFilterTagList :tags="searchTags">
                        <AmountIndicator
                            :amount="newSearchOngoing ? undefined : nrOfSearchResults"
                            class="mr-1 mr-md-2 align-self-baseline"
                        />
                        <span class="search-results__overview__text align-self-baseline">
                            <template v-if="externalResultsSource === 'camera'">
                                resultaten van jouw opname
                            </template>
                            <template v-else>
                                {{ $tc('search.results', nrOfSearchResults) }}
                            </template>
                        </span>
                    </SearchFilterTagList>
                </div>
            </div>
        </div>
        <b-spinner class="align-self-center" v-if="newSearchOngoing" />
        <div v-else>
            <div v-if="nrOfSearchResults" class="row align-items-baseline mt-n5">
                <SearchResultCard
                    class="col-12 col-md-6 col-xl-4 mt-5"
                    v-for="result in sortedSearchResults"
                    :key="result.signId"
                    :searchResult="result"
                    :showFeedbackButton="externalResultsSource === 'camera' && !feedbackGiven"
                    @feedback-selected="onResultFeedbackSelected"
                />
            </div>
            <div v-else class="search-results__no-results d-flex flex flex-column align-items-center">
                <div class="d-flex flex-column flex-sm-row align-items-center mb-2">
                    <NoResults class="search-results__no-results__img mr-3" />
                    <div class="d-flex flex-column">
                        <span class="search-results__no-results__title mt-3">{{ $t('search.no_results') }}</span>
                        <i18n
                            class="search-results__no-results__title__subtext mt-2 mt-sm-0"
                            v-if="nrOfRelatedSearchResult"
                            path="search.related_results_found"
                            tag="span"
                            :places="{ nrOfResults: nrOfRelatedSearchResult }"
                        >
                            <span v-scroll-to="'#relatedResults'" class="link" place="below">
                                {{ $t('search.related_results_found_below') }}
                            </span>
                        </i18n>
                    </div>
                </div>
                <i18n
                    class="search-results__no-results__help my-3"
                    :path="nrOfRelatedSearchResult ? 'search.no_results_help_alternative' : 'search.no_results_help'"
                    tag="span"
                >
                    <span class="link" @click="newSearch" place="new_search">{{ $t('search.new_search') }}</span>
                    <a class="link" :href="faqUrl" target="_blank" place="faq">{{
                        $t('search.no_results_help_page')
                    }}</a>
                </i18n>
            </div>
        </div>
        </div>
        
        <DetectionFeedback 
            ref="detectionFeedback"
            v-if="nrOfSearchResults > 0 && externalResultsSource === 'camera'"
            :searchResults="searchResults"
            :sessionId="searchSessionId"
            :searchSource="externalResultsSource"
            @feedback-submitted="onFeedbackSubmitted"
            class="mt-4"
        />
    </div>
</template>

<script lang="ts">
import NoResults from '@/assets/svg/no-results.svg';
import { sortSearchResults } from '@/util/gloss-sorting';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { ISignOverview } from '../../../../functions/src/shared/contracts/api/GlossContract';
import { Constants } from '../../shared/Constants';
import AmountIndicator from '../AmountIndicator.vue';
import { eventHub } from '../EventHub.vue';
import SearchFilterTagList from './SearchFilterTagList.vue';
import SearchResultCard from './SearchResultCard.vue';
import DetectionFeedback from './DetectionFeedback.vue';
import { ITag } from './TagModel';
import { IDetectionFeedbackData } from '@/shared/interfaces/DetectionFeedback';

const searchModule = namespace('search');

@Component({ components: { AmountIndicator, NoResults, SearchFilterTagList, SearchResultCard, DetectionFeedback } })
export default class SearchResults extends Vue {
    @Prop()
    public nrOfSearchResults!: number;

    @Prop()
    public nrOfRelatedSearchResult!: number;

    @Prop()
    public searchResults!: ISignOverview[];

    @Prop({ required: false, default: null })
    public searchSessionId!: string | null;

    public readonly faqUrl = Constants.faqUrl;
    public feedbackGiven: boolean = false;

    @searchModule.Getter
    public searchTags!: ITag[];

    @searchModule.Getter
    public newSearchOngoing!: boolean;

    @searchModule.Getter
    public externalResultsSource!: 'camera' | null;

    @searchModule.Action
    private clear!: () => Promise<void>;

    @Watch('searchSessionId')
    onSearchSessionChanged(newSessionId: string | null) {
        if (newSessionId) {
            const stored = sessionStorage.getItem(`feedback-given-${newSessionId}`);
            this.feedbackGiven = stored === 'true';
        } else {
            this.feedbackGiven = false;
        }
    }

    @Watch('externalResultsSource')
    onResultsSourceChanged() {
        if (this.searchSessionId) {
            const stored = sessionStorage.getItem(`feedback-given-${this.searchSessionId}`);
            this.feedbackGiven = stored === 'true';
        } else {
            this.feedbackGiven = false;
        }
    }

    public mounted() {
        if (this.searchSessionId) {
            const stored = sessionStorage.getItem(`feedback-given-${this.searchSessionId}`);
            this.feedbackGiven = stored === 'true';
        }
    }

    public newSearch(): void {
        eventHub.emitAppEvent('newSearchTriggered');
        this.clear();
    }

    public async onFeedbackSubmitted(feedbackData: IDetectionFeedbackData): Promise<void> {
        this.feedbackGiven = true;
        
        if (this.searchSessionId) {
            sessionStorage.setItem(`feedback-given-${this.searchSessionId}`, 'true');
        }
        
        await this.$nextTick();
        this.$emit('feedback-submitted', feedbackData);
    }

    public onResultFeedbackSelected(result: ISignOverview): void {
        const detectionFeedback = this.$refs.detectionFeedback as any;
        if (detectionFeedback && detectionFeedback.onResultSelected) {
            detectionFeedback.onResultSelected(result);
        }
    }

    public get sortedSearchResults() {
        const groupedResults = sortSearchResults(this.searchResults);

        let finalSortedResults = [];
        this.searchResults.forEach((item) => {
            if (groupedResults[item.glossName].length > 0) {
                finalSortedResults.push(groupedResults[item.glossName].shift());
            }
        });

        return finalSortedResults;
    }
}
</script>

<style lang="scss">
.search-results__overview {
    border-bottom: $divider;
}

.search-results__overview__inner {
    min-width: 0;
    flex-wrap: wrap;
}

.search-results__overview__text {
    color: $text-title-color;
    font-size: $font-size-medium;
    letter-spacing: 0.075rem;
    white-space: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
    hyphens: auto;
    -webkit-hyphens: auto;
    -ms-hyphens: auto;
    line-height: 1.25;
    @include media-breakpoint-down(sm) {
        font-size: $font-size-default;
    }
    @include media-breakpoint-up(md) {
        font-size: $font-size-big;
    }
}

.search-results__no-results__img {
    height: 5rem;
}

.search-results__no-results__title {
    font-size: 1.375rem;
    font-family: $font-family-accent;
    font-weight: 500;
}

.search-results__no-results__help {
    font-family: $font-family-accent;
    font-weight: 500;
}

.search-results__no-results__new-search {
    color: $accent-color;
}

.search-results__no-results__new-search__icon {
    font-size: 1.75rem;
}
</style>
